// ─────────────────────────────────────────────────────────────────
// PATH B — Client realtime, now Server-Sent Events over the main
// backend at /api/realtime/stream. No separate WebSocket service.
//
// Shape matches the legacy channel API so call-site code doesn't
// change:
//   const ch = realtime.channel('messages:channel-abc');
//   ch.on('insert', row => { ... });
//   await ch.subscribe();
//   // ...
//   ch.unsubscribe();
//
// Implementation:
//   * All open channels multiplex through ONE EventSource. Any time
//     the topic-set changes, we tear the stream down and open a new
//     one with the updated `?topics=` list.
//   * Debounce topic-set changes so a burst of subscribes (typical
//     on page mount) opens the stream once.
//   * EventSource auto-reconnects on server-side close (Vercel
//     maxDuration ends the stream every ~4.5min; the client
//     re-opens seamlessly).
// ─────────────────────────────────────────────────────────────────

import { getAccessToken } from './auth.js';

const _channels = new Map(); // topic → Channel
let _source = null;
let _openTimer = null;

class Channel {
  constructor(topic) {
    this.topic = topic;
    this.handlers = { insert: [], update: [], delete: [] };
    this.subscribed = false;
  }
  on(event, cb) {
    if (!this.handlers[event]) this.handlers[event] = [];
    this.handlers[event].push(cb);
    return this;
  }
  async subscribe() {
    if (this.subscribed) return this;
    this.subscribed = true;
    _scheduleReopen();
    return this;
  }
  unsubscribe() {
    if (!this.subscribed) return;
    this.subscribed = false;
    _channels.delete(this.topic);
    _scheduleReopen();
  }
  _dispatch(event, payload) {
    (this.handlers[event] || []).forEach(cb => {
      try { cb(payload); }
      catch (e) { console.warn('[realtime] handler error on', this.topic, event, e); }
    });
  }
}

function _scheduleReopen() {
  // Coalesce rapid changes — mount pages typically subscribe to 3-5
  // channels in a single tick.
  if (_openTimer) clearTimeout(_openTimer);
  _openTimer = setTimeout(_reopen, 50);
}

function _streamUrl() {
  const override = import.meta.env.VITE_REALTIME_STREAM_URL;
  return override || '/api/realtime/stream';
}

function _reopen() {
  _openTimer = null;

  // Close the old stream (if any) — a topic-set change means we need
  // a new URL, and EventSource URLs are immutable.
  if (_source) {
    try { _source.close(); } catch {}
    _source = null;
  }

  const topics = [..._channels.values()].filter(ch => ch.subscribed).map(ch => ch.topic);
  if (topics.length === 0) return;

  const token = getAccessToken();
  if (!token) {
    // Not logged in yet. Try again shortly — token becomes available
    // once initAuth() resolves.
    setTimeout(_scheduleReopen, 500);
    return;
  }

  const params = new URLSearchParams({
    topics: topics.join(','),
    token,
  });
  const url = `${_streamUrl()}?${params.toString()}`;

  const es = new EventSource(url);
  _source = es;

  es.onmessage = (evt) => {
    let msg;
    try { msg = JSON.parse(evt.data); } catch { return; }
    if (!msg?.topic) return;
    const ch = _channels.get(msg.topic);
    if (!ch) return;
    ch._dispatch(msg.event || 'insert', msg.payload);
  };

  es.onerror = () => {
    // EventSource handles its own retry loop after transient errors.
    // If the browser gives up (readyState === CLOSED), reopen manually.
    if (es.readyState === EventSource.CLOSED) {
      _source = null;
      setTimeout(_scheduleReopen, 2000 + Math.random() * 1000);
    }
  };
}

export const realtime = {
  channel(topic) {
    let ch = _channels.get(topic);
    if (!ch) {
      ch = new Channel(topic);
      _channels.set(topic, ch);
    }
    return ch;
  },
  disconnect() {
    if (_source) { try { _source.close(); } catch {} }
    _source = null;
    _channels.clear();
    if (_openTimer) { clearTimeout(_openTimer); _openTimer = null; }
  },
};
